import type { FC } from "react";
import { useState } from "react";

import { trackRedeemClick } from "@loyalty/utils";

import { GiftIcon } from "@jti/brand-icons/gift";
import { LockOutlineCloseIcon } from "@jti/brand-icons/lock-outline-close";
import { PackageCrossIcon } from "@jti/brand-icons/package-cross";
import { TimeIcon } from "@jti/brand-icons/time";
import { Button } from "@jti/core-components/components/Button";
import { Loader } from "@jti/core-components/components/Loader";
import { PictureBehaviour } from "@jti/core-components/components/PictureBehaviour";
import type { IndexRewardResponseSchema } from "@jti/evo-client";
import { useLoyaltyPrize } from "@jti/rest-api";
import { useSelector } from "@jti/store/reduxHooks";
import { selectIsUserDataFetching } from "@jti/store/selectors/selectIsUserDataFetching";
import { type RootStoreState } from "@jti/store/types";
import { Carousel } from "@jti/ui/components/Carousel";
import { IconDescriptionCard } from "@jti/ui/components/IconDescriptionCard";
import { TierBadge } from "@jti/ui/components/TierBadge";
import { Typography } from "@jti/ui/components/Typography";
import { useIntlNumberFormat } from "@jti/ui/hooks/useIntlNumberFormat";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { validateLimitedTimeBadgesData } from "@jti/ui/utils/validateBadgeData";

import useLoggedUser from "../../hooks/useLoggedUser";
import { usePrizeCta } from "../../hooks/useLoyaltyPrizeCta/useLoyaltyPrizeCta";
import { PointsProgressBar } from "../PointsProgressBar";
import { RedeemDialogV2 } from "../RedeemDialogV2";
import { validateTierRestriction } from "../SimplifiedPrizes/util/simplifedPrizesUtils";

import { useLotteryCountdown } from "./hooks/useLotteryCountdown";

import styles from "./Prize.module.scss";

export type PrizeProps = {
  images: { mobile: string; tablet: string; desktop: string; alt: string }[];
  discoverButtonUrl: string;
  loginButtonUrl: string;
  earnPointsCtaUrl: string;
  newOfferDays: number;
  prize_uuid: string;
  logoUrl?: string;
  logoAlt?: string;
  isInEditor?: boolean;
  prizesPageUrl?: string;
  isLotteryAddressRequirementDisabled?: boolean;
  quantityAriaLabel?: string;
  increaseAriaLabel?: string;
  decreaseAriaLabel?: string;
};

export const Prize: FC<PrizeProps> = ({
  newOfferDays,
  images,
  discoverButtonUrl,
  earnPointsCtaUrl,
  prize_uuid,
  loginButtonUrl,
  isInEditor,
  logoAlt,
  logoUrl,
  prizesPageUrl,
  isLotteryAddressRequirementDisabled,
  quantityAriaLabel,
  increaseAriaLabel,
  decreaseAriaLabel,
}) => {
  const [isDialogOpened, setIsDialogOpened] = useState(false);
  const tier = useSelector((state: RootStoreState) => state.loyaltyCore.tier.name);
  const intl = useIntlNumberFormat();
  const { __ } = useTranslations();
  const {
    points: { balance },
    uuid,
  } = useSelector((state: RootStoreState) => state.loyaltyCore);
  const isUserDataFetching = useSelector(selectIsUserDataFetching);
  const [isLogged] = useLoggedUser();
  const {
    data: rewardResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useLoyaltyPrize({ consumer_uuid: uuid, reward_uuid: prize_uuid });
  const getPrizeCta = usePrizeCta();
  const handleOnClick = (item: IndexRewardResponseSchema) => {
    setIsDialogOpened(true);
    trackRedeemClick({
      name: item.presentation.name,
      locations: item.custom_attributes.locations,
    });
  };

  const prize = rewardResponse?.data;
  const { label: timeLeft, isLotteryExpired } = useLotteryCountdown(prize?.validity_rules?.time?.to);

  if ((isLoading && !prize) || isUserDataFetching) {
    return <Loader data-testid="prizeComponentLoading" />;
  }

  if (isError) {
    console.error(error, "Prize component error");
    return <Typography variant="body1">{__("prizes.api.error")}</Typography>;
  }

  if (!prize) return null;

  const {
    presentation: { images: prizeDataImages = [], name, description },
    identifier,
    brand_name: brandName,
    points,
    custom_attributes: { locations = [], is_address_required },
    has_coupons,
    created_at,
    levels,
    validity_rules: validityRules,
    prize_type,
    usage_limit: { per_user } = {},
    redeemed_times,
  } = prize;

  const { to, from, limited: isTimeLimited } = validityRules?.time ?? { to: null, from: null, limited: null };

  const isOutOfStock = !has_coupons;
  const isDelayedLottery =
    // @ts-expect-error - "delayed_lottery_simplified" will be added in the future EVO versions
    (prize_type === "delayed_lottery_physical" || prize_type === "delayed_lottery_simplified") && isTimeLimited;
  const lowestEligibleTier = levels?.[0];
  const isTierRestricted = isLogged ? validateTierRestriction(tier, levels) : true;

  const { isNewBadgeVisible } = validateLimitedTimeBadgesData({
    __,
    newOfferDays: newOfferDays,
    timeFrom: from ?? undefined,
    timeTo: to ?? undefined,
  });

  const getRibbonText = () => {
    if (isOutOfStock) return __("prizes.out.of.stock.title");
    if (isTimeLimited) return __("prizes.time.limited.ribbon.text");
    if (isNewBadgeVisible) return __("prizes.new.label");
  };

  const ribbonText = getRibbonText();
  const cta = getPrizeCta({
    prizePoints: points,
    balance,
    earnPointsCtaText: __("prizes.earn.points.cta.text"),
    earnPointsCtaUrl,
    ctaLabel: __("prizes.cta.label"),
    onClick: () => handleOnClick(prize),
    type: prize_type,
    redeemedTimes: redeemed_times ?? undefined,
    redeemLimit: per_user ?? undefined,
    isLotteryExpired,
    intl,
  });

  const delayedLotteryBox = isLotteryExpired
    ? {
        icon: GiftIcon,
        title: __("prizes.time.limited.box.ended.title"),
        text: __("prizes.time.limited.box.ended.text"),
      }
    : {
        icon: TimeIcon,
        title: timeLeft,
        text: __("prizes.time.limited.box.text"),
      };

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <div className={styles.prizeComponent} data-testid="prizeComponent">
      {isLogged && (
        <TierBadge
          isAvailable={!isTierRestricted}
          className={styles.tierBadge}
          tier={lowestEligibleTier}
          text={__("prizes.tier.label.text", [lowestEligibleTier])}
        />
      )}
      {ribbonText && <div className={styles.textRibbon}>{ribbonText}</div>}
      <Typography className={styles.prizeTitle} variant="h1">
        {__(name)}
      </Typography>
      <Carousel isOverflowEnabled={false} desktopItemCount={1} tabletItemCount={1} isViewportHeight={true}>
        {images.map((image) => (
          <PictureBehaviour
            key={image.mobile}
            sources={[
              { srcSet: image.desktop || image.mobile, media: "(min-width: 1280px)" },
              { srcSet: image.tablet || image.mobile, media: "(min-width: 768px)" },
            ]}
            imageProps={{
              className: styles.image,
              "data-testid": "prizeComponentImage",
              src: image.mobile,
              alt: image.alt,
            }}
          />
        ))}
      </Carousel>
      <div className={styles.container}>
        <div className={styles.topSubcontainer}>
          <div className={styles.points}>
            <div className={styles.pointsAmount}>{points}</div>
            <div className={styles.pointsText}>{__("prizes.points.label")}</div>
          </div>
          <PointsProgressBar
            pointsNeededText={__("prizes.points.needed.text")}
            requiredPoints={points}
            currentPoints={balance}
            className={styles.pointsProgressBar}
            hidden={!isLogged}
          />
          <div className={styles.cta}>
            {!isLogged && (
              <Button href={loginButtonUrl} variant="primary">
                {__("prizes.login.button")}
              </Button>
            )}
            {isLogged && !isOutOfStock && !isTierRestricted && (
              <Button disabled={cta.disabled} href={cta.href} onClick={cta.onClick} variant="primary">
                {cta.label}
              </Button>
            )}
            <Button variant="secondary" href={discoverButtonUrl}>
              {__("prizes.discover.button.text")}
            </Button>
          </div>
        </div>
        {isLogged && isTierRestricted && (
          <IconDescriptionCard
            icon={LockOutlineCloseIcon}
            title={__("prizes.tier.restricted.title", [lowestEligibleTier])}
            text={__("prizes.tier.restricted.text", [lowestEligibleTier])}
          />
        )}
        {isDelayedLottery && <IconDescriptionCard {...delayedLotteryBox} />}
        {isOutOfStock && (
          <IconDescriptionCard
            icon={PackageCrossIcon}
            title={__("prizes.out.of.stock.title")}
            text={__("prizes.out.of.stock.text")}
          />
        )}
      </div>
      <RedeemDialogV2
        pointsBalance={balance}
        prizeData={{
          prize_type,
          brandName,
          created_at,
          has_coupons,
          images: prizeDataImages,
          levels,
          locations,
          name,
          points,
          prize_uuid: identifier,
          subtitle: description ?? "",
          time_from: from ?? undefined,
          time_to: to ?? undefined,
          isAddressRequired: is_address_required,
        }}
        isDialogOpen={isDialogOpened}
        onCloseDialog={() => setIsDialogOpened(false)}
        logoAlt={logoAlt ?? ""}
        logoUrl={logoUrl ?? ""}
        isInEditor={isInEditor}
        isLotteryAddressRequirementDisabled={isLotteryAddressRequirementDisabled}
        prizesPageUrl={prizesPageUrl}
        handlePrizePurchase={handleRefetch}
        quantityAriaLabel={quantityAriaLabel}
        increaseAriaLabel={increaseAriaLabel}
        decreaseAriaLabel={decreaseAriaLabel}
      />
    </div>
  );
};
